import { useMemo, useState } from "react";
import { toast } from "../../utils/toast";
import { abroadAdminApi } from "../../api/abroadLoans";
import { errorMessageOf } from "../../api/scholarships";
import { canManageScholarships } from "../scholarships/access";
import { useFieldChanges } from "../calls/forms";
import SaveChangesBar from "../calls/SaveChangesBar";
import { detailEditableIn, detailErrorsOf, detailsFormOf, detailsOf, useCallSaved, validateDetails } from "./model";
import CallDetailsFields from "./CallDetailsFields";

// A loan call's details; unsaved edits are kept per call (only the changed fields, never the loaded data)
export default function DetailsTab({ call }) {
  const saved = useCallSaved();
  const original = useMemo(() => detailsFormOf(call), [call]);
  const { values, changedFields, errors, setErrors, update, discard } = useFieldChanges(`abroad-call-edit:${call.id}`, original);
  const [saving, setSaving] = useState(false);

  const savedYear = useMemo(() => ({ id: call.academicYearId, name: call.academicYearName }), [call]);
  const manage = canManageScholarships();
  const canEdit = manage ? detailEditableIn(call) : () => false;

  const save = async (e) => {
    e.preventDefault();
    const found = validateDetails(values);
    setErrors(found);
    if (Object.keys(found).length) return;

    setSaving(true);
    try {
      const { data } = await abroadAdminApi.changeCall(call.id, detailsOf(values));
      saved(data);
      toast.success("Details saved");
      discard();
    } catch (error) {
      console.error(`Failed to update loan call ${call.id}:`, error);
      setErrors(detailErrorsOf(error));
      toast.error(errorMessageOf(error, "Failed to save the loan call. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const field = (name) => ({ name, value: values[name], onChange: update, error: errors[name], disabled: saving || !canEdit(name) });

  return (
    <form onSubmit={save} noValidate className="flex flex-col gap-5">
      <CallDetailsFields field={field} values={values} savedYear={savedYear} />

      {manage && call.state !== "CANCELLED" && (
        <SaveChangesBar count={changedFields.length} saving={saving} onDiscard={discard} className="border-t border-slate-100 pt-5" />
      )}
    </form>
  );
}
